import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Mail, Phone, Github, Linkedin, Twitter, Instagram } from "lucide-react";
import { CONTACT, NAV_LINKS, SERVICES } from "@/data/content";
import { subscribeNewsletter } from "@/lib/api";

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await subscribeNewsletter(email);
      toast.success("You're on the list.", { description: "Welcome to the TrustaFiesta insider notes." });
      setEmail("");
    } catch (err) {
      const msg = err?.response?.status === 409 ? "You're already subscribed." : "Something went wrong. Try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="relative overflow-hidden border-t border-white/10 bg-ink" data-testid="footer">
      <div className="pointer-events-none absolute inset-0 bg-grid opacity-40 mask-fade-b" />
      <div className="pointer-events-none absolute -top-32 left-1/2 h-64 w-[80%] -translate-x-1/2 rounded-full bg-gold/10 blur-[120px]" />

      <div className="container-x relative py-20">
        <div className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3">
              <img src={CONTACT.logo} alt="TrustaFiesta Tech" className="h-12 w-12 rounded-lg object-cover ring-1 ring-white/10" />
              <span className="font-heading text-xl font-extrabold text-white">
                Trusta<span className="text-gold">Fiesta</span> Tech
              </span>
            </div>
            <p className="mt-5 max-w-sm text-white/60">
              A luxury software studio building modern websites, AI-powered applications and scalable
              software for ambitious teams worldwide.
            </p>

            <form onSubmit={onSubscribe} className="mt-8 max-w-sm" data-testid="newsletter-form">
              <label className="label-mono">Newsletter</label>
              <div className="mt-3 flex items-center gap-2 rounded-full border border-white/10 bg-white/5 p-1.5 pl-5 focus-within:border-gold/50">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  data-testid="newsletter-input"
                  className="w-full bg-transparent text-sm text-white placeholder:text-white/40 focus:outline-none"
                />
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="newsletter-submit"
                  className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-gold text-ink transition-colors hover:bg-gold-light disabled:opacity-60"
                  aria-label="Subscribe"
                >
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </form>
          </div>

          <div className="grid grid-cols-2 gap-8 sm:grid-cols-3 lg:col-span-7">
            <div>
              <h4 className="label-mono">Company</h4>
              <ul className="mt-5 space-y-3">
                {NAV_LINKS.map((l) => (
                  <li key={l.to}>
                    <Link to={l.to} className="text-sm text-white/60 transition-colors hover:text-gold" data-testid={`footer-link-${l.label.toLowerCase()}`}>
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="label-mono">Services</h4>
              <ul className="mt-5 space-y-3">
                {SERVICES.slice(0, 6).map((s) => (
                  <li key={s.title}>
                    <Link to="/services" className="text-sm text-white/60 transition-colors hover:text-gold">
                      {s.title.replace(" Development", "")}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="label-mono">Get in touch</h4>
              <ul className="mt-5 space-y-3">
                <li>
                  <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-2 text-sm text-white/60 hover:text-gold">
                    <Mail className="h-4 w-4" /> {CONTACT.email}
                  </a>
                </li>
                <li>
                  <a href={CONTACT.whatsappHref} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-white/60 hover:text-gold">
                    <Phone className="h-4 w-4" /> {CONTACT.whatsapp}
                  </a>
                </li>
              </ul>
              <div className="mt-6 flex gap-3">
                {[Twitter, Linkedin, Github, Instagram].map((Icon, i) => (
                  <a key={i} href="#" aria-label="social" className="grid h-10 w-10 place-items-center rounded-full border border-white/10 text-white/70 transition-colors hover:border-gold/50 hover:text-gold">
                    <Icon className="h-4 w-4" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Massive brand wordmark */}
      <div className="container-x relative pb-10">
        <div className="select-none border-t border-white/10 pt-10 text-center">
          <span className="block bg-gradient-to-b from-white/[0.08] to-transparent bg-clip-text font-heading text-[18vw] font-black leading-none text-transparent">
            TRUSTAFIESTA
          </span>
        </div>
        <div className="flex flex-col items-center justify-between gap-3 pt-6 text-xs text-white/40 sm:flex-row">
          <span>© {new Date().getFullYear()} TrustaFiesta Tech. All rights reserved.</span>
          <div className="flex gap-6">
            <span className="cursor-pointer hover:text-white/70">Privacy Policy</span>
            <span className="cursor-pointer hover:text-white/70">Terms</span>
          </div>
        </div>
      </div>
    </footer>
  );
};
